import React, { useState, useEffect } from 'react';
import { Settings, RotateCcw, X, Loader2, Mail, FileText, RefreshCw, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SaleFinalizationModal } from './SaleFinalizationModal';

interface Client {
  id: number;
  nombre: string;
  cuil_cuit: string;
  condicion_fiscal: string;
  email?: string;
  direccion?: string;
}

interface Local {
  id: number;
  nombre: string;
}

interface Caja {
  id: number;
  nombre: string;
}

interface SaleItem {
  variantId: number;
  productName: string;
  variantName: string;
  quantity: number;
  price: number;
  discounts?: {
    discountId: number;
    discountName: string;
    discountValue: number;
    discountType: string;
    minQuantity?: number | null;
    maxQuantity?: number | null;
    calculoDescuento: string;
    valorDescuento: number;
  }[];
}

interface SalesSummaryProps {
  items: SaleItem[];
  onUpdateQuantity: (variantId: number, quantity: number) => void;
  onRemoveItem: (variantId: number) => void;
  onReset: () => void;
}

export const SalesSummary: React.FC<SalesSummaryProps> = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onReset
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // Used to determine document type (receipt vs. invoice)
  const [receiptType, setReceiptType] = useState<'comprobante' | 'factura'>('comprobante');
  const [invoiceType, setInvoiceType] = useState<'CF' | 'B' | 'A' | null>('CF');
  const [showInvoiceTypeDropdown, setShowInvoiceTypeDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locales, setLocales] = useState<Local[]>([]);
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [selectedLocal, setSelectedLocal] = useState<number | null>(null);
  const [selectedCaja, setSelectedCaja] = useState<number | null>(null);
  const [newClient, setNewClient] = useState<Partial<Client>>({});
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{
    id: number;
    metodo_de_pago: string;
    comision: number;
  } | null>(null);
  const [showFinalizationModal, setShowFinalizationModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<{
    id: number;
    metodo_de_pago: string;
    comision: number;
  }[]>([]);
  const [isComprobanteSelected, setIsComprobanteSelected] = useState(false);
  const [isFacturaSelected, setIsFacturaSelected] = useState(false);
  const [companyConfig, setCompanyConfig] = useState<{
    condicion_fiscal: string;
    monto_min_facturacion_cash: number;
  } | null>(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Client search functionality has been removed

  const fetchCompanyConfig = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: config, error: configError } = await supabase
        .from('config')
        .select('condicion_fiscal, monto_min_facturacion_cash, monto_min_facturacion_card')
        .eq('company_id', profile.id_company)
        .single();

      if (configError) throw configError;
      setCompanyConfig(config);
    } catch (err) {
      console.error('Error fetching company config:', err);
      setError(err instanceof Error ? err.message : 'Error loading company configuration');
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      await fetchCompanyConfig();

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      // Fetch locales
      const { data: localesData } = await supabase
        .from('locales')
        .select('id, nombre')
        .eq('company_id', profile.id_company)
        .eq('tipo', 'PoS')
        .order('nombre');

      setLocales(localesData || []);
      if (!selectedLocal && localesData && localesData.length > 0) {
        setSelectedLocal(localesData[0].id);
      }

      // Fetch cajas
      const { data: cajasData } = await supabase
        .from('cajas')
        .select('id, nombre')
        .eq('company_id', profile.id_company);

      // Fetch payment methods
      const { data: methodsData } = await supabase
        .from('metodos_de_pago')
        .select('id, metodo_de_pago, comision')
        .eq('company_id', profile.id_company)
        .order('metodo_de_pago');

      setPaymentMethods(methodsData || []);

      setCajas(cajasData || []);
      if (!selectedCaja && cajasData && cajasData.length > 0) {
        setSelectedCaja(cajasData[0].id);
      }

    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  // Client search functionality has been removed

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // config_users table removed; no persistence needed
      // Dispatch event to notify SalesRegister that local has changed
      const localChangedEvent = new Event('local-config-changed');
      window.dispatchEvent(localChangedEvent);
      
      setShowSettings(false);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Error saving settings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: client, error: insertError } = await supabase
        .from('clientes')
        .insert({ ...newClient, company_id: profile.id_company })
        .select()
        .single();

      if (insertError) throw insertError;

      setSelectedClient(client);
      setShowNewClientModal(false);
      setNewClient({});
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err instanceof Error ? err.message : 'Error creating client');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    // Calculate subtotal from item prices before discounts
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Calculate total discounts amount
    const totalDiscounts = items.reduce((sum, item) => {
      const itemDiscounts = item.discounts?.reduce((dSum, discount) => dSum + discount.discountValue, 0) || 0;
      return sum + itemDiscounts;
    }, 0);
    
    // Apply discounts to final amount
    const subtotalAfterDiscounts = subtotal - totalDiscounts;
    
    const iva = subtotalAfterDiscounts * 0.21;
    const neto = subtotalAfterDiscounts / 1.21;
    const commission = selectedPaymentMethod ? (subtotalAfterDiscounts * selectedPaymentMethod.comision) / 100 : 0;
    const total = subtotalAfterDiscounts + commission;
    
    return { subtotal, totalDiscounts, subtotalAfterDiscounts, iva, neto, commission, total };
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(price);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">Resumen de la Venta</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button
            onClick={onReset}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Invoice Type Selection */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Cliente
        </label>
        <div className="relative">
          <button
            onClick={() => setShowInvoiceTypeDropdown(!showInvoiceTypeDropdown)}
            className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span>
              {invoiceType === 'CF' && 'Consumidor Final'}
              {invoiceType === 'B' && 'Monotributista'}
              {invoiceType === 'A' && 'Responsable Inscripto/Exento'}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>
          
          {showInvoiceTypeDropdown && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              <div
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-xs"
                onClick={() => {
                  setInvoiceType('CF');
                  setShowInvoiceTypeDropdown(false);
                }}
              >
                Consumidor Final
              </div>
              <div
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-xs"
                onClick={() => {
                  setInvoiceType('B');
                  setShowInvoiceTypeDropdown(false);
                }}
              >
                Monotributista
              </div>
              <div
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer text-xs"
                onClick={() => {
                  setInvoiceType('A');
                  setShowInvoiceTypeDropdown(false);
                }}
              >
                Responsable Inscripto/Exento
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Client Selection input removed as requested */}

      {/* Receipt Type */}
      <div className="mb-6">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Tipo de Comprobante
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setIsComprobanteSelected(!isComprobanteSelected);
              setIsFacturaSelected(false);
              setReceiptType('comprobante');
            }}
            className={`flex-1 px-4 py-1.5 rounded-lg border ${
              isComprobanteSelected
                ? 'bg-green-50 border-green-500 text-green-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Comprobante de venta
          </button>
          <button
            onClick={() => {
              setIsFacturaSelected(!isFacturaSelected);
              setIsComprobanteSelected(false);
              setReceiptType('factura');
            }}
            className={`flex-1 px-4 py-1.5 rounded-lg border ${
              isFacturaSelected
                ? 'bg-green-50 border-green-500 text-green-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {invoiceType === 'A' ? 'Factura A' : 'Factura B'}
          </button>
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto mb-6">
        {items.map((item) => (
          <div
            key={item.variantId}
            className="flex flex-col p-3 border-b last:border-b-0"
          >
            {/* Main item row */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onUpdateQuantity(item.variantId, item.quantity - 1)}
                  className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                >
                  -
                </button>
                <span className="w-8 text-center">{item.quantity}</span>
                <button
                  onClick={() => onUpdateQuantity(item.variantId, item.quantity + 1)}
                  className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                >
                  +
                </button>
              </div>
              <div className="flex-1">
                <div className="font-medium">{item.productName}</div>
                <div className="text-sm text-gray-500">{item.variantName}</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{formatPrice(item.price * item.quantity)}</div>
                <button
                  onClick={() => onRemoveItem(item.variantId)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Discount rows */}
            {item.discounts && item.discounts.length > 0 && (
              <div className="pl-12 mt-1">
                {item.discounts.map((discount) => (
                  <div key={discount.discountId} className="flex justify-between items-center text-sm text-red-600">
                    <div className="italic">Descuento: {discount.discountName}</div>
                    <div className="font-medium">- {formatPrice(discount.discountValue)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Payment Method */}
      <div className="mb-6">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Método de Pago
        </label>
        <select
          value={selectedPaymentMethod?.id || ''}
          onChange={(e) => {
            const method = paymentMethods.find(m => m.id === Number(e.target.value));
            setSelectedPaymentMethod(method || null);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Seleccionar método de pago</option>
          {paymentMethods.map(method => (
            <option key={method.id} value={method.id}>
              {method.metodo_de_pago} {method.comision > 0 ? `(Comisión: ${method.comision}%)` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Totals */}
      <div className="border-t pt-4 space-y-2">
        <div className="flex justify-between text-xs">
          <span>Subtotal</span>
          <span>{formatPrice(calculateTotals().subtotal)}</span>
        </div>
        
        {/* Show discounts if any */}
        {calculateTotals().totalDiscounts > 0 && (
          <div className="flex justify-between text-xs text-red-600 font-medium">
            <span>Descuentos</span>
            <span>- {formatPrice(calculateTotals().totalDiscounts)}</span>
          </div>
        )}
        
        {calculateTotals().totalDiscounts > 0 && (
          <div className="flex justify-between text-xs font-medium">
            <span>Subtotal con descuentos</span>
            <span>{formatPrice(calculateTotals().subtotalAfterDiscounts)}</span>
          </div>
        )}
        
        <div className="flex justify-between text-xs">
          <span>Neto</span>
          <span>{formatPrice(calculateTotals().neto)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span>IVA (21%)</span>
          <span>{formatPrice(calculateTotals().iva)}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span>Recargo por método de pago</span>
          <span>{selectedPaymentMethod ? `${selectedPaymentMethod.comision}%` : '0%'}</span>
        </div>
        <div className="flex justify-between font-medium text-base border-t pt-2 mt-2">
          <span>Total a pagar</span>
          <span>{formatPrice(calculateTotals().total)}</span>
        </div>
        <button
          className="w-full mt-4 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={
            items.length === 0 || 
            loading || 
            !selectedPaymentMethod ||
            (!isComprobanteSelected && !isFacturaSelected)
          }
          onClick={() => {
            // Use receipt type to determine document finalization flow
            console.log(`Finalizing sale with receipt type: ${receiptType}`);
            setShowFinalizationModal(true);
          }}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : (
            'FINALIZAR VENTA'
          )}
        </button>
      </div>

      {/* Sale Finalization Modal */}
      {showFinalizationModal && (
        <SaleFinalizationModal
          isOpen={showFinalizationModal}
          onClose={() => setShowFinalizationModal(false)}
          selectedClient={selectedClient}
          selectedPaymentMethod={selectedPaymentMethod}
          items={items}
          totals={calculateTotals()}
          companyConfig={companyConfig}
          localId={selectedLocal}
          cajaId={selectedCaja}
          invoiceType={invoiceType}
          receiptType={receiptType}
          onSuccess={() => {
            setShowFinalizationModal(false);
            onReset();
          }}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-medium">Configuración de Venta</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Punto de Venta
                </label>
                <select
                  value={selectedLocal || ''}
                  onChange={(e) => setSelectedLocal(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar punto de venta</option>
                  {locales.map((local) => (
                    <option key={local.id} value={local.id}>
                      {local.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cuenta donde ingresa el cobro
                </label>
                <select
                  value={selectedCaja || ''}
                  onChange={(e) => setSelectedCaja(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar caja</option>
                  {cajas.map((caja) => (
                    <option key={caja.id} value={caja.id}>
                      {caja.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={!selectedLocal || !selectedCaja || loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Guardar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-medium">Nuevo Cliente</h3>
              <button
                onClick={() => setShowNewClientModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={newClient.nombre || ''}
                  onChange={(e) => setNewClient({ ...newClient, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DNI *
                </label>
                <input
                  type="text"
                  value={newClient.dni || ''}
                  onChange={(e) => setNewClient({ ...newClient, dni: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newClient.email || ''}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram
                </label>
                <input
                  type="text"
                  value={newClient.instagram || ''}
                  onChange={(e) => setNewClient({ ...newClient, instagram: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={newClient.telefono || ''}
                  onChange={(e) => setNewClient({ ...newClient, telefono: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  value={newClient.direccion || ''}
                  onChange={(e) => setNewClient({ ...newClient, direccion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowNewClientModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateClient}
                disabled={!newClient.nombre || !newClient.dni || loading}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Crear Cliente'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};